import { createPinia } from "pinia";
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";

// Fontawesome
import FontAwesomeButton from "@/shared/FontAwesomeButton.vue";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
    faGoogle,
    faJs,
    faMarkdown,
    faPython,
} from "@fortawesome/free-brands-svg-icons";
import {
    faArrowAltCircleDown,
    faComments,
    faFile,
    faFileAlt,
    faFileArchive,
    faFilePdf,
    faImage,
    faWindowClose,
} from "@fortawesome/free-regular-svg-icons";
import {
    faAngleLeft,
    faAngleRight,
    faBold,
    faCheck,
    faCode,
    faCog,
    faCopy,
    faDatabase,
    faEdit,
    faFolder,
    faFolderOpen,
    faHistory,
    faIndent,
    faItalic,
    faLayerGroup,
    faLink,
    faList,
    faListOl,
    faOutdent,
    faProjectDiagram,
    faQuestion,
    faQuoteLeft,
    faRedo,
    faSearch,
    faStrikethrough,
    faTasks,
    faTimes,
    faUnderline,
    faUndo,
    faVial,
    faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Font
import "@fontsource/plus-jakarta-sans";

import mitt from "mitt";
const emitter = mitt();

// Highlight.js
import hljsVuePlugin from "@highlightjs/vue-plugin";
import hljs from "highlight.js";
// TODO: Choose a highlight theme (ll node_modules/highlight.js/styles)
import * as Sentry from "@sentry/vue";
import "highlight.js/styles/googlecode.css";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { initPrimeVue } from "./primevue/init";
import mixpanel from "mixpanel-browser";

// create an instance of the Mixpanel object
mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN ?? "dummy", {
    debug: false,
    ignore_dnt: false, // This honors the Do Not Track setting in the browser. Need to set this to false for GDPR compliance.
    api_host: "https://api-eu.mixpanel.com", // This is the EU server. We need to use this for GDPR compliance.
    loaded: (mixpanel) => {
        if (mixpanel.get_config("token") === "dummy") {
            mixpanel.disable();
        }
    },
});

export const app = createApp(App);
initPrimeVue(app);
app.use(router);

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app.use(pinia);

if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.VITE_SENTRY_ENV) {
    Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENV,
        integrations: [Sentry.replayIntegration()],

        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

// Mitt
app.config.globalProperties.emitter = emitter;

// Highlight.js
hljs.registerAliases("bpmn", { languageName: "xml" });
app.use(hljsVuePlugin);

// Fontawesome
app.component("FontAwesomeIcon", FontAwesomeIcon);
app.component("FontAwesomeButton", FontAwesomeButton);
library.add(
    faAngleLeft,
    faAngleRight,
    faArrowAltCircleDown,
    faBold,
    faCheck,
    faCode,
    faCog,
    faComments,
    faCopy,
    faDatabase,
    faEdit,
    faFile,
    faFileAlt,
    faFileArchive,
    faFilePdf,
    faFolder,
    faFolderOpen,
    faGoogle,
    faHistory,
    faImage,
    faIndent,
    faItalic,
    faJs,
    faLayerGroup,
    faLink,
    faList,
    faListOl,
    faMarkdown,
    faOutdent,
    faProjectDiagram,
    faPython,
    faQuestion,
    faQuoteLeft,
    faRedo,
    faSearch,
    faStrikethrough,
    faTasks,
    faTimes,
    faUnderline,
    faUndo,
    faVial,
    faVideo,
    faWindowClose
);

app.mount("#app");
